import { Controller } from "@hotwired/stimulus"
import { Loggable } from "./concerns/loggable"

const CACHE_NAME = "learn-offline-videos-v1"
const MANIFEST_KEY = "learn.offlineVideos"
const OFFLINE_ACCESS_DAYS = 30
const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000

export default class extends Controller {
  static targets = ["controls", "saveButton", "removeButton", "playerLink", "status"]

  static values = {
    available: Boolean,
    downloadingLabel: String,
    endpoint: String,
    errorLabel: String,
    offlineAccessExpiredLabel: String,
    postId: String,
    savedLabel: String,
    savingLabel: String,
    title: String,
    userPresent: { type: Boolean, default: true }
  }

  connect() {
    Loggable(this, {debug: true})
    this.log("connected", {postId: this.postIdValue, available: this.availableValue})

    if (!this.availableValue) {
      this.log("offline video unavailable for this post")
      this.hideControls()
      return
    }

    if (!this.userPresentValue) {
      this.log("offline video unavailable without current user")
      this.hideControls()
      return
    }

    if (!this.supported()) {
      this.log("offline video unsupported in this browser")
      this.hideControls()
      return
    }

    this.showControls()
    this.renderRemoved()

    this.refreshSavedState()
  }

  async save() {
    if (!this.userPresentValue) {
      this.log("save skipped because current user is missing")
      this.hideControls()
      return
    }

    if (!this.supported()) {
      this.log("save skipped because offline video is unsupported")
      return
    }

    this.log("saving offline video", {postId: this.postIdValue})
    this.saving = true
    this.renderSaving()

    try {
      await this.requestPersistentStorage()

      const metadata = await this.fetchMetadata()
      this.log("offline video metadata fetched", {
        postId: this.postIdValue,
        rendition: metadata.rendition,
        size: metadata.size,
        expiresAt: metadata.expires_at
      })

      const cache = await caches.open(CACHE_NAME)

      const storedVideoRequest = this.storedVideoRequest()
      const videoResponse = await this.fetchVideoResponse(metadata.video_url, metadata.size)
      this.log("offline video response fetched", {
        postId: this.postIdValue,
        type: videoResponse.type,
        status: videoResponse.status
      })
      await cache.put(storedVideoRequest, videoResponse)
      this.log("offline video file cached", {
        postId: this.postIdValue,
        rendition: metadata.rendition,
        size: metadata.size
      })

      this.saveManifestEntry(metadata, {
        storedVideoUrl: this.storedVideoUrl()
      })
      this.renderSaved()
    } catch (error) {
      this.error("save failed", error)
      this.renderRemoved()
      this.setStatus(this.errorLabelValue || "Could not save offline")
    } finally {
      this.saving = false
    }
  }

  async remove() {
    if (!this.userPresentValue) {
      this.log("remove skipped because current user is missing")
      this.hideControls()
      return
    }

    if (!this.supported()) {
      this.log("remove skipped because offline video is unsupported")
      return
    }

    this.log("removing offline video", {postId: this.postIdValue})
    const entry = this.manifestEntry()
    await this.deleteCachedEntry(entry)

    this.removeManifestEntry()
    this.renderRemoved()
  }

  supported() {
    return Boolean(window.caches && window.localStorage && this.hasEndpointValue)
  }

  async fetchMetadata() {
    const response = await fetch(this.endpointValue, {headers: {"Accept": "application/json"}})
    if (!response.ok) {
      this.error("metadata request failed", {status: response.status})
      throw new Error(`offline video unavailable (${response.status})`)
    }

    return response.json()
  }

  saveManifestEntry(metadata, storage = {}) {
    const manifest = this.readManifest()

    manifest[this.postIdValue] = {
      postId: this.postIdValue,
      title: metadata.title || this.titleValue,
      videoUrl: metadata.video_url,
      storedVideoUrl: storage.storedVideoUrl,
      rendition: metadata.rendition,
      size: metadata.size,
      expiresAt: metadata.expires_at,
      savedAt: new Date().toISOString(),
      offlineAccessExpiresAt: this.offlineAccessExpiresAt().toISOString()
    }

    this.writeManifest(manifest)
  }

  manifestEntry() {
    return this.readManifest()[this.postIdValue]
  }

  removeManifestEntry() {
    const manifest = this.readManifest()
    delete manifest[this.postIdValue]
    this.writeManifest(manifest)
  }

  async refreshSavedState() {
    const entry = await this.savedManifestEntry({
      renew: this.shouldRenewOfflineAccess(),
      renderExpired: true
    })
    if (this.saving) return

    if (!entry) {
      if (!this.expiredRendered) this.renderRemoved()
      return
    }

    this.log("offline cached video found", {postId: this.postIdValue})
    this.renderSaved()
  }

  async savedManifestEntry({renew = false, renderExpired = false} = {}) {
    const entry = this.manifestEntry()
    if (!entry) return null

    this.log("offline manifest entry found", {postId: this.postIdValue})

    if (!await this.cachedEntryExists(entry)) {
      this.log("stale offline manifest entry removed", {postId: this.postIdValue})
      await this.deleteCachedEntry(entry)
      this.removeManifestEntry()
      return null
    }

    if (this.offlineAccessExpired(entry)) {
      if (renew) {
        this.log("renewing expired offline access from original player", {postId: this.postIdValue})
        return this.renewManifestEntry(entry)
      }

      this.log("expired offline access removed", {postId: this.postIdValue})
      await this.deleteCachedEntry(entry)
      this.removeManifestEntry()
      if (renderExpired) this.renderExpired()
      return null
    }

    if (renew) {
      this.log("renewing offline access from original player", {postId: this.postIdValue})
      return this.renewManifestEntry(entry)
    }

    return entry
  }

  async cachedEntryExists(entry) {
    if (!entry.storedVideoUrl) return false

    try {
      const cache = await caches.open(CACHE_NAME)
      const videoResponse = await cache.match(this.storedVideoRequest(entry.storedVideoUrl))
      return Boolean(videoResponse)
    } catch (_error) {
      return false
    }
  }

  readManifest() {
    try {
      return JSON.parse(localStorage.getItem(MANIFEST_KEY)) || {}
    } catch (_error) {
      return {}
    }
  }

  writeManifest(manifest) {
    localStorage.setItem(MANIFEST_KEY, JSON.stringify(manifest))
  }

  shouldRenewOfflineAccess() {
    return navigator.onLine !== false && !document.documentElement.hasAttribute("data-turbo-preview")
  }

  offlineAccessExpiresAt(now = new Date()) {
    return new Date(now.getTime() + (OFFLINE_ACCESS_DAYS * DAY_IN_MILLISECONDS))
  }

  offlineAccessExpired(entry, now = new Date()) {
    if (!Object.prototype.hasOwnProperty.call(entry || {}, "offlineAccessExpiresAt")) return false

    const expiresAt = new Date(entry.offlineAccessExpiresAt)
    return Number.isNaN(expiresAt.getTime()) || now.getTime() >= expiresAt.getTime()
  }

  renewManifestEntry(entry) {
    const manifest = this.readManifest()
    const renewedEntry = {
      ...entry,
      offlineAccessExpiresAt: this.offlineAccessExpiresAt().toISOString()
    }

    manifest[this.postIdValue] = renewedEntry
    this.writeManifest(manifest)

    return renewedEntry
  }

  async deleteCachedEntry(entry) {
    const cache = await caches.open(CACHE_NAME)

    if (entry?.storedVideoUrl) {
      await cache.delete(this.storedVideoRequest(entry.storedVideoUrl))
    } else if (entry?.videoUrl) {
      await cache.delete(this.videoRequest(entry.videoUrl))
    }
  }

  videoRequest(url) {
    return new Request(url, {mode: "no-cors"})
  }

  storedVideoUrl() {
    return `/offline-videos/${encodeURIComponent(this.postIdValue)}.mp4`
  }

  storedVideoRequest(url = this.storedVideoUrl()) {
    return new Request(url, {mode: "no-cors"})
  }

  async fetchVideoResponse(url, expectedSize) {
    try {
      const response = await fetch(url)
      if (!response.ok) {
        this.error("video request failed", {status: response.status})
        throw new Error(`offline video fetch failed (${response.status})`)
      }

      return this.responseWithDownloadProgress(response, expectedSize)
    } catch (error) {
      if (!(error instanceof TypeError)) throw error

      this.log("cors video fetch unavailable; falling back to no-cors")
      this.renderDownloadProgress(0, expectedSize)
      return fetch(this.videoRequest(url))
    }
  }

  responseWithDownloadProgress(response, expectedSize) {
    const totalBytes = this.downloadTotalBytes(response, expectedSize)
    this.renderDownloadProgress(0, totalBytes)

    if (
      !response.body?.pipeThrough ||
      typeof TransformStream === "undefined" ||
      typeof Response === "undefined"
    ) {
      return response
    }

    let downloadedBytes = 0
    const progressStream = new TransformStream({
      transform: (chunk, controller) => {
        downloadedBytes += this.chunkSize(chunk)
        this.renderDownloadProgress(downloadedBytes, totalBytes)
        controller.enqueue(chunk)
      }
    })

    return new Response(response.body.pipeThrough(progressStream), {
      headers: response.headers,
      status: response.status,
      statusText: response.statusText
    })
  }

  downloadTotalBytes(response, expectedSize) {
    return this.positiveInteger(expectedSize) || this.positiveInteger(response.headers?.get?.("Content-Length"))
  }

  positiveInteger(value) {
    const integer = Number.parseInt(value, 10)
    return Number.isFinite(integer) && integer > 0 ? integer : null
  }

  chunkSize(chunk) {
    if (chunk?.byteLength !== undefined) return chunk.byteLength
    if (chunk?.length !== undefined) return chunk.length

    return 0
  }

  renderDownloadProgress(downloadedBytes, totalBytes) {
    const label = this.downloadingLabelValue || this.savingLabelValue || "Downloading"
    const progress = [this.formatBytes(downloadedBytes)]

    if (totalBytes) {
      progress.push(this.formatBytes(totalBytes))
    }

    this.setStatus(`${label} ${progress.join(" / ")}`)
  }

  formatBytes(bytes) {
    const units = ["B", "KB", "MB", "GB"]
    let value = Math.max(bytes, 0)
    let unitIndex = 0

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024
      unitIndex += 1
    }

    return `${this.formatNumber(value)} ${units[unitIndex]}`
  }

  formatNumber(value) {
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: value < 10 ? 1 : 0
    }).format(value)
  }

  async requestPersistentStorage() {
    if (!navigator.storage?.persisted || !navigator.storage?.persist) return false

    try {
      if (await navigator.storage.persisted()) {
        this.log("persistent storage already granted")
        return true
      }

      const granted = await navigator.storage.persist()
      this.log("persistent storage requested", {granted})
      return granted
    } catch (_error) {
      this.log("persistent storage request unavailable")
      return false
    }
  }

  showControls() {
    if (this.hasControlsTarget) {
      this.controlsTarget.classList.remove("d-none")
    }
  }

  hideControls() {
    if (this.hasControlsTarget) {
      this.controlsTarget.classList.add("d-none")
    }
  }

  renderSaved() {
    this.log("rendering saved state", {postId: this.postIdValue})

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.add("d-none")
    }
    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.classList.remove("d-none")
    }
    if (this.hasPlayerLinkTarget) {
      this.playerLinkTarget.classList.remove("d-none")
    }

    this.setStatus(this.savedLabelValue || "Saved offline")
    this.hideStatus()
  }

  renderSaving() {
    this.log("rendering saving state", {postId: this.postIdValue})

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.add("d-none")
    }
    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.classList.add("d-none")
    }
    if (this.hasPlayerLinkTarget) {
      this.playerLinkTarget.classList.add("d-none")
    }

    this.showStatus()
    this.setStatus(this.savingLabelValue || "Saving offline")
  }

  renderRemoved() {
    this.expiredRendered = false
    this.log("rendering removed state", {postId: this.postIdValue})

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.remove("d-none")
    }
    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.classList.add("d-none")
    }
    if (this.hasPlayerLinkTarget) {
      this.playerLinkTarget.classList.add("d-none")
    }

    this.showStatus()
    this.setStatus("")
  }

  renderExpired() {
    this.expiredRendered = true
    this.log("rendering expired offline access state", {postId: this.postIdValue})

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.add("d-none")
    }
    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.classList.add("d-none")
    }
    if (this.hasPlayerLinkTarget) {
      this.playerLinkTarget.classList.add("d-none")
    }

    this.showStatus()
    this.setStatus(this.offlineAccessExpiredLabelValue || "Offline access expired. Open Learn online to renew access.")
  }

  setStatus(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
    }
  }

  hideStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add("visually-hidden")
    }
  }

  showStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove("visually-hidden")
    }
  }
}
